import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

interface OnlineStatusContextValue {
  isOnline: boolean;
  justReconnected: boolean;
  dismissReconnected: () => void;
}

const OnlineStatusContext = createContext<OnlineStatusContextValue>({
  isOnline: true,
  justReconnected: false,
  dismissReconnected: () => {},
});

export function useOnlineStatus() {
  return useContext(OnlineStatusContext);
}

export function OnlineStatusProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [wasOffline, setWasOffline] = useState(false);
  const [justReconnected, setJustReconnected] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        setJustReconnected(true);
        setWasOffline(false);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  const dismissReconnected = useCallback(() => {
    setJustReconnected(false);
  }, []);

  return (
    <OnlineStatusContext.Provider value={{ isOnline, justReconnected, dismissReconnected }}>
      {children}
    </OnlineStatusContext.Provider>
  );
}